"use client";

import {
  Checkbox,
  Panel,
  PillGroup,
  SelectField,
} from "@/components/tool-kit/primitives";
import type { RICH_TEXT_TO_LATEX_STRINGS } from "@/components/tools/rich-text-to-latex";
import type {
  HeadingScheme,
  RichTextOptions,
  TableRules,
  UnicodeMode,
} from "@/lib/tool-kit/latex-dev/rich-text-inline";

type Strings = typeof RICH_TEXT_TO_LATEX_STRINGS;

const SCHEMES: readonly HeadingScheme[] = ["section", "chapter"];
const RULES: readonly TableRules[] = ["booktabs", "hline", "grid", "none"];
const UNICODE: readonly UnicodeMode[] = ["keep", "ligatures"];

export function RichTextOptionsPanel({
  options,
  onChange,
  strings,
}: {
  options: RichTextOptions;
  onChange: (next: RichTextOptions) => void;
  strings: Strings;
}) {
  const set = <K extends keyof RichTextOptions>(
    key: K,
    value: RichTextOptions[K]
  ) => onChange({ ...options, [key]: value });
  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Panel title={strings.textHeading}>
        <PillGroup
          label={strings.schemeLabel}
          name="rtl-scheme"
          onChange={(value) => set("headingScheme", value)}
          options={SCHEMES.map((value) => ({
            value,
            label: strings.schemes[value],
          }))}
          value={options.headingScheme}
        />
        <SelectField
          id="rtl-unicode"
          label={strings.unicodeLabel}
          onChange={(value) => set("unicode", value)}
          options={UNICODE.map((value) => ({
            value,
            label: strings.unicodeModes[value],
          }))}
          value={options.unicode}
        />
        <div className="flex flex-col gap-2">
          <Checkbox
            checked={options.numberedHeadings}
            label={strings.numbered}
            onChange={(value) => set("numberedHeadings", value)}
          />
          <Checkbox
            checked={options.fixQuotes}
            label={strings.fixQuotes}
            onChange={(value) => set("fixQuotes", value)}
          />
          <Checkbox
            checked={options.fullDocument}
            label={strings.fullDocument}
            onChange={(value) => set("fullDocument", value)}
          />
        </div>
      </Panel>
      <Panel title={strings.tablesHeading}>
        <SelectField
          id="rtl-rules"
          label={strings.rulesLabel}
          onChange={(value) => set("tableRules", value)}
          options={RULES.map((value) => ({
            value,
            label: strings.rules[value],
          }))}
          value={options.tableRules}
        />
        <div className="flex flex-col gap-2">
          <Checkbox
            checked={options.tableFloat}
            label={strings.tableFloat}
            onChange={(value) => set("tableFloat", value)}
          />
          <Checkbox
            checked={options.boldHeader}
            label={strings.boldHeader}
            onChange={(value) => set("boldHeader", value)}
          />
          <Checkbox
            checked={options.firstRowHeader}
            label={strings.firstRowHeader}
            onChange={(value) => set("firstRowHeader", value)}
          />
        </div>
      </Panel>
    </div>
  );
}
